import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
  signal,
} from '@angular/core'
import { form } from '@angular/forms/signals'
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { AsiakasService, AsiakkaanAvainTiedot } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitoImageHandlerService, TositeJaNaytettavaSivut } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { AsiakasJaettuService } from 'app/_jaettu-lemonator/service/asiakas-jaettu.service'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { TilikarttaJaettuService } from 'app/_jaettu-lemonator/service/tilikartta-jaettu.service'
import { BehaviorSubject, firstValueFrom, Observable, of, Subject } from 'rxjs'
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators'
import { Asiakas } from '../../../_jaettu-lemonator/model/asiakas'
import {
  AlvMaaritys,
  AsiakkaanTilikartta,
  Kirjanpitotili,
  KirjanpitotilinAlvTyyppi,
  KirjaukseenLiitettyjenTiedostojenSivut,
  Kirjaus,
  Kirjausrivi,
  MyyntiAlvt,
  OstoAlvt,
  Paatilikartta,
} from '../../../_jaettu-lemonator/model/kirjanpito'
import { LlmTestisettiKokoelma, LlmTestitapaus, LlmTestitapausKategoria, LlmTestitapausOikeaVaihtoehto } from '../../../_jaettu-lemonator/model/llm-kirjausehdotus-testaus'
import { AlvMaaritysFinder, DocIdProvider } from '../../../_jaettu-lemonator/service/alv-lemonator-base.service'
import { KirjanpitoKirjausService } from '../../../_jaettu-lemonator/service/kirjanpito-kirjaus.service'
import { callLlmTestaus } from '../llm-kirjausehdotus-testaus.client'

const _ALV_TILINUMEROT = new Set(['1763', '2939', '2946'])

type Solusarake = 'tili' | 'debet' | 'kredit' | 'alv'

@Component({
  selector: 'app-kategoria-setit',
  templateUrl: './kategoria-setit.component.html',
  styleUrls: ['../../../kirjanpito/kirjanpito.component.css', './kategoria-setit.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KategoriaSetitComponent implements OnInit, OnChanges, OnDestroy {
  @Input() kategoria!: LlmTestitapausKategoria
  @Input() setit: LlmTestisettiKokoelma[] = []
  @Input() setitLataa = false
  @Input() valittuSetti: LlmTestisettiKokoelma | null = null
  @Input() settiTestitapaukset: LlmTestitapaus[] = []
  @Input() settiTestitapauksetLataa = false
  @Input() poistetaanJasenyys: ReadonlySet<string> = new Set()
  @Input() luoSettiKaynnissa = false
  @Input() uusiSettiNimi = ''

  @Output() uusiSettiNimiChange = new EventEmitter<string>()
  @Output() luoSetti = new EventEmitter<void>()
  @Output() poistaSetti = new EventEmitter<LlmTestisettiKokoelma>()
  @Output() avaaSetti = new EventEmitter<LlmTestisettiKokoelma>()
  @Output() suljeSetti = new EventEmitter<void>()
  @Output() poistaJasenyys = new EventEmitter<LlmTestitapaus>()

  @ViewChild('tositeContainer', { static: false }) tositeContainerRef: ElementRef<HTMLDivElement>

  @ViewChild('tnroInput', { read: ElementRef, static: false }) private _tnroInput: ElementRef<HTMLInputElement>
  @ViewChild('debetInput', { read: ElementRef, static: false }) private _debetInput: ElementRef<HTMLInputElement>
  @ViewChild('kreditInput', { read: ElementRef, static: false }) private _kreditInput: ElementRef<HTMLInputElement>
  @ViewChild('alvInput', { read: ElementRef, static: false }) private _alvInput: ElementRef<HTMLInputElement>

  @ViewChild('tnroInput', { read: MatAutocompleteTrigger, static: false }) private _tiliAutocompleteTrigger: MatAutocompleteTrigger
  @ViewChild('alvInput', { read: MatAutocompleteTrigger, static: false }) private _alvAutocompleteTrigger: MatAutocompleteTrigger

  @ViewChild('tiliAuto', { static: false }) private _tiliAutocomplete: MatAutocomplete
  @ViewChild('alvAuto', { static: false }) private _alvAutocomplete: MatAutocomplete

  private _ngUnsubscribe = new Subject<void>()
  private _valittuTestitapausSubject = new Subject<LlmTestitapaus | null>()

  private readonly _kirjausService: KirjanpitoKirjausService
  private readonly _alvFinder: AlvMaaritysFinder
  private readonly _docIdProvider: DocIdProvider
  private readonly _alvLyhenteenMukaan = new Map<string, AlvMaaritys>()
  private readonly _alvTunnisteenMukaan = new Map<string, AlvMaaritys>()

  readonly tyhjaKirjausObservable: Observable<null> = of(null)

  readonly valittuTestitapaus = signal<LlmTestitapaus | null>(null)
  readonly naytettavatTositteetJaSivut = signal<TositeJaNaytettavaSivut[] | null>([])

  readonly muokattavaTestitapaus = signal<LlmTestitapaus | null>(null)
  readonly muokattavaKirjaus = signal<Kirjaus | null>(null)
  /** Jos editoidaan olemassaolevaa oikeaa vaihtoehtoa, sen avain — muuten pää-kirjausta tai uutta vaihtoehtoa. */
  readonly muokattavaVaihtoehtoAvain = signal<string | null>(null)
  /** True kun parhaillaan luodaan uutta oikeaa vaihtoehtoa (ei vielä tallennettu). */
  readonly muokattavaOnUusiVaihtoehto = signal(false)
  readonly muutoksia = signal(false)
  readonly tilikarttaLataa = signal(false)
  readonly tallennetaan = signal(false)

  private _tilinumeroSubject = new BehaviorSubject<string | null>(null)
  private _alvSubject = new BehaviorSubject<string | null>(null)
  private _suodatetutTilitSubject = new BehaviorSubject<Kirjanpitotili[]>([])
  private _suodatetutAlvtSubject = new BehaviorSubject<AlvMaaritys[]>([])
  suodatetutTilitObservable: Observable<Kirjanpitotili[]> = this._suodatetutTilitSubject.asObservable()
  suodatetutAlvtObservable: Observable<AlvMaaritys[]> = this._suodatetutAlvtSubject.asObservable()

  private _aktiivisetAlvt: AlvMaaritys[] = []
  private _handlingIflight = false

  private _tilitList: Kirjanpitotili[] = []
  private _tilitMap = new Map<string, Kirjanpitotili>()
  private _riviMap = new Map<string, Kirjausrivi>()
  private readonly _tiliNimetAsiakkaittain = signal<ReadonlyMap<string, Map<string, string>>>(new Map())
  private readonly _changeDetectorRef = inject(ChangeDetectorRef)
  private _paatilikarttaPromise: Promise<Paatilikartta> | null = null

  uusiSettiNimiKentta = form(signal(''))

  constructor(
    private _dateService: DateService,
    private _firebase: FirebaseLemonator,
    private _asiakasService: AsiakasService,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _kirjanpitoImageHandlerService: KirjanpitoImageHandlerService,
    private _currencyService: CurrencyService,
    private _tilikarttaJaettuService: TilikarttaJaettuService,
    private _asiakasJaettuService: AsiakasJaettuService,
    private _snackbar: MatSnackBar,
    private _dialog: MatDialog,
  ) {
    this._kirjausService = new KirjanpitoKirjausService(this._currencyService)
    for (const alv of [...MyyntiAlvt.kaikki, ...OstoAlvt.kaikki]) {
      this._alvTunnisteenMukaan.set(alv.tunniste, alv)
      this._alvLyhenteenMukaan.set(alv.lyhenne.toLowerCase(), alv)
    }
    this._alvFinder = { annaMaaritys: async tunniste => this._alvTunnisteenMukaan.get(tunniste) as AlvMaaritys }
    this._docIdProvider = { annaId: () => this._firebase.firestoreCreateId() }
    arvonMuutokset(this.uusiSettiNimiKentta).subscribe(nimi => this.uusiSettiNimiChange.emit(nimi))
  }

  ngOnInit() {
    this._valittuTestitapausSubject
      .pipe(
        tap(() => this.naytettavatTositteetJaSivut.set(null)),
        switchMap(testitapaus => {
          if (!testitapaus) {
            return of<TositeJaNaytettavaSivut[]>([])
          }
          return this._asiakasService.annaAsiakasObservable(testitapaus.asiakasAvain).pipe(
            take(1),
            switchMap(asiakas => {
              if (!asiakas) {
                return of<TositeJaNaytettavaSivut[]>([])
              }
              const asiakkaanAvainTiedot: AsiakkaanAvainTiedot = { avain: asiakas.avain, asiakasId: asiakas.asiakasId }
              const uri = this._kirjanpitoUriService.annaKirjaukseenLiitettyjenTiedostojenUri(testitapaus.asiakasAvain, testitapaus.kirjausAvain)
              return this._firebase
                .firestoreDoc<KirjaukseenLiitettyjenTiedostojenSivut>(uri)
                .listen()
                .pipe(switchMap(sivut => this._kirjanpitoImageHandlerService.muunnaTositteiksiJaSivuiksi(asiakkaanAvainTiedot, sivut, null, null, false)))
            }),
          )
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(tositteet => {
        this.naytettavatTositteetJaSivut.set(tositteet)
      })

    this._tilinumeroSubject
      .pipe(
        map(haku => {
          const suodatetut = haku !== null ? this.suodataTilit(haku) : []
          const tili = this._tilitMap.get((haku ?? '').trim())
          if (tili && suodatetut.length === 1) {
            if (this._tiliAutocomplete?.isOpen) {
              this._tiliAutocompleteTrigger?.closePanel()
            }
            return []
          }
          if (suodatetut.length > 0 && this._tiliAutocomplete && !this._tiliAutocomplete.isOpen) {
            setTimeout(() => this._tiliAutocompleteTrigger?.openPanel(), 50)
          }
          return suodatetut
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(suodatetut => this._suodatetutTilitSubject.next(suodatetut))

    this._alvSubject
      .pipe(
        map(haku => {
          const suodatetut = haku !== null ? this._suodataAlvt(haku) : []
          const alv = this._alvLyhenteenMukaan.get((haku ?? '').trim().toLowerCase())
          if (alv && suodatetut.length < 2) {
            if (this._alvAutocomplete?.isOpen) {
              this._alvAutocompleteTrigger?.closePanel()
            }
            return []
          }
          if (suodatetut.length > 0 && this._alvAutocomplete && !this._alvAutocomplete.isOpen) {
            setTimeout(() => this._alvAutocompleteTrigger?.openPanel(), 50)
          }
          return suodatetut
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(suodatetut => this._suodatetutAlvtSubject.next(suodatetut))
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['uusiSettiNimi']) {
      asetaArvo(this.uusiSettiNimiKentta, this.uusiSettiNimi ?? '', { ilmoita: false })
    }
    if (changes['settiTestitapaukset'] || changes['valittuSetti']) {
      const valittu = this.valittuTestitapaus()
      if (valittu && !this.settiTestitapaukset.some(t => t.avain === valittu.avain)) {
        this.valittuTestitapaus.set(null)
        this._valittuTestitapausSubject.next(null)
      }
      const muokattava = this.muokattavaTestitapaus()
      if (muokattava && !this.settiTestitapaukset.some(t => t.avain === muokattava.avain)) {
        this.peruutaMuokkaus()
      }
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  valitseTestitapaus(testitapaus: LlmTestitapaus) {
    this._lataaTilinimet(testitapaus.asiakasAvain, testitapaus.kirjaus.p)
    if (this.valittuTestitapaus()?.avain === testitapaus.avain) {
      return
    }
    this.valittuTestitapaus.set(testitapaus)
    this._valittuTestitapausSubject.next(testitapaus)
  }

  formatPvm(p: number): string {
    return this._dateService.muotoileNumberPaiva(p, 'fi')
  }

  kirjanpitoVuosi(p: number): number {
    return this._dateService.numberToLocalDate(p)?.year
  }

  kirjanpitoKuukausi(p: number): number {
    return this._dateService.numberToLocalDate(p)?.month
  }

  debetYhteensa(rivit: Kirjausrivi[]): number {
    return rivit.reduce((s, r) => s + (r.d ?? 0), 0)
  }

  kreditYhteensa(rivit: Kirjausrivi[]): number {
    return rivit.reduce((s, r) => s + (r.k ?? 0), 0)
  }

  muokkausTasapainossa(): boolean {
    if (!this.muokattavaKirjaus()) {
      return true
    }
    const debet = this._currencyService.roundHalfUp(this.debetYhteensa(this.muokattavaKirjaus().rivit), 2)
    const kredit = this._currencyService.roundHalfUp(this.kreditYhteensa(this.muokattavaKirjaus().rivit), 2)
    return debet === kredit
  }

  onkoMuokattavana(testitapaus: LlmTestitapaus): boolean {
    return this.muokattavaTestitapaus()?.avain === testitapaus.avain
  }

  /** Editoidaanko parhaillaan tämän testitapauksen ensisijaista (pää-)vastausta. */
  onkoPaaMuokattavana(testitapaus: LlmTestitapaus): boolean {
    return this.onkoMuokattavana(testitapaus) && !this.muokattavaVaihtoehtoAvain() && !this.muokattavaOnUusiVaihtoehto()
  }

  /** Editoidaanko parhaillaan tiettyä olemassaolevaa oikeaa vaihtoehtoa. */
  onkoVaihtoehtoMuokattavana(testitapaus: LlmTestitapaus, vaihtoehtoAvain: string): boolean {
    return this.onkoMuokattavana(testitapaus) && this.muokattavaVaihtoehtoAvain() === vaihtoehtoAvain
  }

  get tallennaPainikeTeksti(): string {
    if (this.muokattavaOnUusiVaihtoehto()) {
      return 'Lisää vaihtoehto'
    }
    return this.muokattavaVaihtoehtoAvain() ? 'Tallenna vaihtoehto' : 'Tallenna'
  }

  onAlvRivi(rivi: Kirjausrivi): boolean {
    return !!rivi.t && _ALV_TILINUMEROT.has(rivi.t)
  }

  annaTilinNimi(tilinumero: string | null, asiakasAvain?: string): string {
    if (!tilinumero) {
      return ''
    }
    if (asiakasAvain) {
      return this._tiliNimetAsiakkaittain().get(asiakasAvain)?.get(tilinumero) ?? ''
    }
    const tili = this._tilitMap.get(tilinumero)
    return tili ? this._tilikarttaJaettuService.annaKirjanpitotilinNimi(tili, 'fi') : ''
  }

  private async _lataaTilinimet(asiakasAvain: string, pvm: number) {
    if (this._tiliNimetAsiakkaittain().has(asiakasAvain)) {
      return
    }
    this._tiliNimetAsiakkaittain.update(kokoelma => new Map(kokoelma).set(asiakasAvain, new Map()))
    try {
      const [asiakas, paatilikartta, asiakkaanTilikartta] = await Promise.all([
        firstValueFrom(this._asiakasService.annaAsiakasObservable(asiakasAvain).pipe(take(1))),
        this._annaPaatilikartta(),
        this._firebase.firestoreDoc<AsiakkaanTilikartta>(this._kirjanpitoUriService.annaAsiakkaanTilikartanUri(asiakasAvain)).get(),
      ])
      const tilikausi = asiakas ? this._asiakasJaettuService.annaTilikausiPaivalle(asiakas as Asiakas, this._dateService.numberToLocalDate(pvm)) : null
      const tilit = tilikausi && paatilikartta ? this._tilikarttaJaettuService.yhdistaAsiakkaantilikarttaPaatilikarttaan(paatilikartta, asiakkaanTilikartta, tilikausi) : []
      const nimet = new Map<string, string>()
      for (const tili of tilit) {
        nimet.set(tili.numero, this._tilikarttaJaettuService.annaKirjanpitotilinNimi(tili, 'fi'))
      }
      this._tiliNimetAsiakkaittain.update(kokoelma => new Map(kokoelma).set(asiakasAvain, nimet))
    } catch (err) {
      console.error('tilinimien lataus epäonnistui', err)
      this._tiliNimetAsiakkaittain.update(kokoelma => new Map([...kokoelma].filter(([avain]) => avain !== asiakasAvain)))
    }
  }

  private _annaPaatilikartta(): Promise<Paatilikartta> {
    if (!this._paatilikarttaPromise) {
      this._paatilikarttaPromise = this._firebase.firestoreDoc<Paatilikartta>('admin/paatilikartta').get()
    }
    return this._paatilikarttaPromise
  }

  annaAlvLyhenne(rivi: Kirjausrivi): string {
    return this._alvLyhenneTunnisteelle(rivi.a)
  }

  // ---------------------------------------------------------------------------
  // Muokkauksen aloitus
  // ---------------------------------------------------------------------------

  private async _vahvistaHylkaysJosTarpeen(): Promise<boolean> {
    if (!this.muutoksia()) {
      return true
    }
    const data: AreYouSureDialogData = { text: 'Hylätäänkö tallentamattomat muutokset?', header: 'Tallentamattomat muutokset', rightAction: 'Hylkää' }
    return !!(await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed()))
  }

  /** Aloittaa ensisijaisen vastauksen muokkauksen tietystä solusta (solu-klikkaus). */
  async aloitaMuokkausSolusta(testitapaus: LlmTestitapaus, rivi: Kirjausrivi, sarake: Solusarake) {
    if (!this.onkoPaaMuokattavana(testitapaus)) {
      if (!(await this._vahvistaHylkaysJosTarpeen())) {
        return
      }
      const onnistui = await this._aloitaMuokkaus(testitapaus)
      if (!onnistui) {
        return
      }
    }
    if (this.onAlvRivi(rivi)) {
      return
    }
    setTimeout(() => this._kohdistaSoluun(rivi.av, sarake), 0)
  }

  /** Aloittaa olemassaolevan oikean vaihtoehdon muokkauksen tietystä solusta. */
  async aloitaVaihtoehtoMuokkausSolusta(testitapaus: LlmTestitapaus, vaihtoehto: LlmTestitapausOikeaVaihtoehto, rivi: Kirjausrivi, sarake: Solusarake) {
    if (!this.onkoVaihtoehtoMuokattavana(testitapaus, vaihtoehto.avain)) {
      if (!(await this._vahvistaHylkaysJosTarpeen())) {
        return
      }
      if (!(await this._aloitaMuokkausVaihtoehto(testitapaus, vaihtoehto))) {
        return
      }
    }
    if (this.onAlvRivi(rivi)) {
      return
    }
    setTimeout(() => this._kohdistaSoluun(rivi.av, sarake), 0)
  }

  /** Aloittaa uuden oikean vaihtoehdon luonnin pää-kirjauksen pohjalta. */
  async aloitaUusiVaihtoehto(testitapaus: LlmTestitapaus) {
    if (!(await this._vahvistaHylkaysJosTarpeen())) {
      return
    }
    if (!(await this._aloitaMuokkaus(testitapaus)) || !this.muokattavaKirjaus()) {
      return
    }
    this.muokattavaOnUusiVaihtoehto.set(true)
    this.muutoksia.set(true)
    this._kohdistaEnsimmaiseenSoluun()
  }

  private async _aloitaMuokkausVaihtoehto(testitapaus: LlmTestitapaus, vaihtoehto: LlmTestitapausOikeaVaihtoehto): Promise<boolean> {
    if (!(await this._aloitaMuokkaus(testitapaus)) || !this.muokattavaKirjaus()) {
      return false
    }
    this.muokattavaKirjaus.set({ ...this.muokattavaKirjaus(), rivit: structuredClone(vaihtoehto.rivit) })
    this.muokattavaVaihtoehtoAvain.set(vaihtoehto.avain)
    this._rakennaRiviMap()
    return true
  }

  private _kohdistaEnsimmaiseenSoluun() {
    const eka = this.muokattavaKirjaus()?.rivit.find(r => !this.onAlvRivi(r))
    if (eka) {
      setTimeout(() => this._kohdistaSoluun(eka.av, 'tili'), 0)
    }
  }

  private async _aloitaMuokkaus(testitapaus: LlmTestitapaus): Promise<boolean> {
    this.valitseTestitapaus(testitapaus)
    this.muokattavaTestitapaus.set(testitapaus)
    this.muokattavaKirjaus.set(structuredClone(testitapaus.kirjaus))
    this.muokattavaVaihtoehtoAvain.set(null)
    this.muokattavaOnUusiVaihtoehto.set(false)
    this.muutoksia.set(false)
    this.tilikarttaLataa.set(true)
    try {
      const [asiakas, paatilikartta, asiakkaanTilikartta] = await Promise.all([
        firstValueFrom(this._asiakasService.annaAsiakasObservable(testitapaus.asiakasAvain).pipe(take(1))),
        this._annaPaatilikartta(),
        this._firebase.firestoreDoc<AsiakkaanTilikartta>(this._kirjanpitoUriService.annaAsiakkaanTilikartanUri(testitapaus.asiakasAvain)).get(),
      ])
      const tilikausi = asiakas ? this._asiakasJaettuService.annaTilikausiPaivalle(asiakas as Asiakas, this._dateService.numberToLocalDate(this.muokattavaKirjaus().p)) : null
      this._tilitList = tilikausi && paatilikartta ? this._tilikarttaJaettuService.yhdistaAsiakkaantilikarttaPaatilikarttaan(paatilikartta, asiakkaanTilikartta, tilikausi) : []
      this._tilitMap = new Map(this._tilitList.map(t => [t.numero, t]))
      this._rakennaRiviMap()
      return true
    } catch (err) {
      console.error('aloitaMuokkaus virhe', err)
      this._snackbar.open('Tilikartan lataus epäonnistui', 'Sulje', { duration: 4000 })
      this.peruutaMuokkaus()
      return false
    } finally {
      this.tilikarttaLataa.set(false)
    }
  }

  peruutaMuokkaus() {
    this.muokattavaTestitapaus.set(null)
    this.muokattavaKirjaus.set(null)
    this.muokattavaVaihtoehtoAvain.set(null)
    this.muokattavaOnUusiVaihtoehto.set(false)
    this.muutoksia.set(false)
    this._tilitList = []
    this._tilitMap = new Map()
    this._riviMap.clear()
    this._aktiivisetAlvt = []
    this._tilinumeroSubject.next(null)
    this._alvSubject.next(null)
  }

  private _rakennaRiviMap() {
    this._riviMap.clear()
    for (const rivi of this.muokattavaKirjaus()?.rivit ?? []) {
      this._riviMap.set(rivi.av, rivi)
    }
  }

  // ---------------------------------------------------------------------------
  // Solujen aktivointi (focusin) — jaettu syöte siirretään aktiivisen solun päälle
  // ---------------------------------------------------------------------------

  @HostListener('focusin', ['$event'])
  soluunSiirryttiin(event: FocusEvent) {
    if (!this.muokattavaKirjaus()) {
      return
    }
    const target = event.target as HTMLElement
    if (target.classList.contains('knc')) {
      this._aktivoiTili(target, target.querySelector('.kn') as HTMLElement)
    } else if (target.classList.contains('kn')) {
      this._aktivoiTili(target.parentElement as HTMLElement, target)
    } else if (target.classList.contains('kd')) {
      const rivi = this._riviMap.get(this._etsiArvoavain(target))
      if (rivi && !this.onAlvRivi(rivi)) {
        this._focusInput(this._debetInput.nativeElement, target, this._formatRaha(rivi.d))
      }
    } else if (target.classList.contains('kk')) {
      const rivi = this._riviMap.get(this._etsiArvoavain(target))
      if (rivi && !this.onAlvRivi(rivi)) {
        this._focusInput(this._kreditInput.nativeElement, target, this._formatRaha(rivi.k))
      }
    } else if (target.classList.contains('ka')) {
      const rivi = this._riviMap.get(this._etsiArvoavain(target))
      if (rivi && !this.onAlvRivi(rivi)) {
        this._aktiivisetAlvt = this._annaAktiivisetAlvt(rivi)
        const lyhenne = this._alvLyhenneTunnisteelle(rivi.a)
        this._focusInput(this._alvInput.nativeElement, target, lyhenne)
        this._alvSubject.next(lyhenne)
      }
    }
  }

  private _aktivoiTili(kncElement: HTMLElement, knElement: HTMLElement) {
    const rivi = this._riviMap.get(this._etsiArvoavain(kncElement))
    if (rivi && !this.onAlvRivi(rivi) && knElement) {
      this._focusInput(this._tnroInput.nativeElement, knElement, rivi.t ?? '')
      this._tilinumeroSubject.next(rivi.t ?? '')
    }
  }

  private _focusInput(input: HTMLInputElement, targetElement: HTMLElement, value: string) {
    this._suljeAutot(input)
    targetElement.parentElement.insertBefore(input, targetElement)
    targetElement.style.display = 'none'
    input.style.display = 'block'
    const kirjausElement = this._etsiKirjausElement(targetElement)
    kirjausElement?.classList.add('active')
    input.focus()
    input.value = value
    input.select()
  }

  @HostListener('focusout', ['$event'])
  solustaPoistuttiin(event: FocusEvent) {
    const target = event.target as HTMLElement
    if (target === this._tnroInput?.nativeElement) {
      this._tiliBlur(target as HTMLInputElement)
      this._unfocusInput(this._tnroInput.nativeElement)
    } else if (target === this._debetInput?.nativeElement) {
      this._unfocusInput(this._debetInput.nativeElement)
    } else if (target === this._kreditInput?.nativeElement) {
      this._unfocusInput(this._kreditInput.nativeElement)
    } else if (target === this._alvInput?.nativeElement) {
      this._alvBlur(target as HTMLInputElement)
      this._unfocusInput(this._alvInput.nativeElement)
    }
  }

  private _unfocusInput(input: HTMLInputElement) {
    const div = input.nextElementSibling as HTMLElement
    if (div) {
      div.style.display = 'block'
      const kirjausElement = this._etsiKirjausElement(div)
      kirjausElement?.classList.remove('active')
    }
    input.style.display = 'none'
  }

  // ---------------------------------------------------------------------------
  // Syötteen muutos (input) — ohjataan suodatusta luokan perusteella
  // ---------------------------------------------------------------------------

  @HostListener('input', ['$event'])
  arvoaMuutettiin(event: Event) {
    if (!this.muokattavaKirjaus()) {
      return
    }
    const target = event.target as HTMLInputElement
    if (target.classList.contains('kni')) {
      this._tilinumeroSubject.next((target.value ?? '').trim())
    } else if (target.classList.contains('kdi')) {
      this._debetMuuttui(target, target.value)
    } else if (target.classList.contains('kki')) {
      this._kreditMuuttui(target, target.value)
    } else if (target.classList.contains('kai')) {
      this._alvSubject.next((target.value ?? '').trim())
    }
  }

  private _debetMuuttui(input: HTMLInputElement, value: string) {
    const rivi = this._riviMap.get(this._etsiArvoavain(input))
    if (!rivi) {
      return
    }
    rivi.d = this._parseRaha(value)
    if (rivi.d !== null) {
      rivi.k = null
    }
    this._paivitaAlv(rivi, false)
    this.muutoksia.set(true)
  }

  private _kreditMuuttui(input: HTMLInputElement, value: string) {
    const rivi = this._riviMap.get(this._etsiArvoavain(input))
    if (!rivi) {
      return
    }
    rivi.k = this._parseRaha(value)
    if (rivi.k !== null) {
      rivi.d = null
    }
    this._paivitaAlv(rivi, false)
    this.muutoksia.set(true)
  }

  // ---------------------------------------------------------------------------
  // Autocompleten valinnat ja kentistä poistuminen
  // ---------------------------------------------------------------------------

  async tiliValittiin(event: MatAutocompleteSelectedEvent) {
    const input = this._tnroInput.nativeElement
    const rivi = this._riviMap.get(this._etsiArvoavain(input))
    this._suljeTiliAuto()
    if (rivi) {
      await this.valitseTili(rivi, event.option.value)
    }
    this._unfocusInput(input)
  }

  async alvValittiin(event: MatAutocompleteSelectedEvent) {
    const input = this._alvInput.nativeElement
    const rivi = this._riviMap.get(this._etsiArvoavain(input))
    this._suljeAlvAuto()
    if (rivi) {
      await this.valitseAlv(rivi, event.option.value)
    }
    this._unfocusInput(input)
  }

  private _tiliBlur(input: HTMLInputElement) {
    const rivi = this._riviMap.get(this._etsiArvoavain(input))
    if (!rivi) {
      return
    }
    const syote = (input.value ?? '').trim()
    if (syote === (rivi.t ?? '')) {
      return
    }
    if (this._tilitMap.has(syote) && !_ALV_TILINUMEROT.has(syote)) {
      this.valitseTili(rivi, syote)
    } else if (!syote) {
      rivi.t = null
      this._paivitaAlv(rivi, false)
      this.muutoksia.set(true)
    }
  }

  private _alvBlur(input: HTMLInputElement) {
    const rivi = this._riviMap.get(this._etsiArvoavain(input))
    if (!rivi) {
      return
    }
    const syote = (input.value ?? '').trim().toLowerCase()
    const alv = this._alvLyhenteenMukaan.get(syote) ?? null
    if ((alv?.tunniste ?? null) === (rivi.a ?? null)) {
      return
    }
    if (alv || !syote) {
      this.valitseAlv(rivi, alv?.lyhenne ?? '')
    }
  }

  async valitseTili(rivi: Kirjausrivi, tilinumero: string) {
    const tili = this._tilitMap.get(tilinumero) ?? null
    rivi.t = tili?.numero ?? null
    if (tili) {
      if (tili.alvTyyppi === KirjanpitotilinAlvTyyppi.EI_ALV_KASITTELYA) {
        rivi.a = null
      } else if (tili.oletusAlvKasittely) {
        rivi.a = tili.oletusAlvKasittely.tunniste
      }
    }
    await this._paivitaAlv(rivi, false)
    this.muutoksia.set(true)
  }

  async valitseAlv(rivi: Kirjausrivi, lyhenne: string) {
    const alv = this._alvLyhenteenMukaan.get(lyhenne.toLowerCase()) ?? null
    rivi.a = alv?.tunniste ?? null
    await this._paivitaAlv(rivi, true)
    this.muutoksia.set(true)
  }

  // ---------------------------------------------------------------------------
  // Näppäimistönavigaatio
  // ---------------------------------------------------------------------------

  @HostListener('keydown.tab', ['$event'])
  @HostListener('keydown.enter', ['$event'])
  siirraKohdistusSeuraavaan(event: Event) {
    if (!this.muokattavaKirjaus() || this._handlingIflight) {
      return
    }
    if (this._tiliAutocomplete?.isOpen || this._alvAutocomplete?.isOpen) {
      return
    }
    const element = event.target as HTMLElement
    if (!element.classList.contains('kni') && !element.classList.contains('kdi') && !element.classList.contains('kki') && !element.classList.contains('kai')) {
      return
    }
    this._handlingIflight = true
    this._preventEvent(event)

    const rivi = this._riviMap.get(this._etsiArvoavain(element))
    const seuraava = this._seuraavaFokusoitavaFrom(element)
    if (seuraava) {
      seuraava.focus()
      this._handlingIflight = false
    } else if (rivi && !this._onkoRiviTyhja(rivi)) {
      this.lisaaRivi()
      setTimeout(() => {
        const uusiRivi = this.muokattavaKirjaus()?.rivit[this.muokattavaKirjaus().rivit.length - 1]
        if (uusiRivi) {
          this._kohdistaSoluun(uusiRivi.av, 'tili')
        }
        this._handlingIflight = false
      }, 0)
    } else {
      this._handlingIflight = false
    }

    this._suljeTiliAuto()
    this._suljeAlvAuto()
  }

  @HostListener('keydown.shift.backspace', ['$event'])
  poistaRiviPikanappaimella(event: Event) {
    if (!this.muokattavaKirjaus()) {
      return
    }
    const element = event.target as HTMLElement
    const rivi = this._riviMap.get(this._etsiArvoavain(element))
    if (rivi && !this.onAlvRivi(rivi) && this._muokattavienRivienMaara() > 1) {
      this._preventEvent(event)
      this.poistaRivi(rivi)
    }
  }

  lisaaRivi() {
    if (!this.muokattavaKirjaus()) {
      return
    }
    const rivi: Kirjausrivi = { av: this._docIdProvider.annaId(), t: null, d: null, k: null, a: null }
    this.muokattavaKirjaus().rivit.push(rivi)
    this._rakennaRiviMap()
    this.muutoksia.set(true)
  }

  poistaRivi(rivi: Kirjausrivi) {
    if (!this.muokattavaKirjaus()) {
      return
    }
    const index = this.muokattavaKirjaus().rivit.indexOf(rivi)
    if (index < 0) {
      return
    }
    let poistettavia = 1
    while (this._onkoSeuraavaAlvRivi(index + poistettavia)) {
      poistettavia++
    }
    this.muokattavaKirjaus().rivit.splice(index, poistettavia)
    this._rakennaRiviMap()
    this.muutoksia.set(true)
  }

  private _onkoSeuraavaAlvRivi(index: number): boolean {
    const rivi = this.muokattavaKirjaus()?.rivit[index]
    return !!rivi && this.onAlvRivi(rivi)
  }

  private _muokattavienRivienMaara(): number {
    return (this.muokattavaKirjaus()?.rivit ?? []).filter(r => !this.onAlvRivi(r)).length
  }

  private _onkoRiviTyhja(rivi: Kirjausrivi): boolean {
    return !rivi.t && rivi.d === null && rivi.k === null
  }

  // ---------------------------------------------------------------------------
  // Suodatus
  // ---------------------------------------------------------------------------

  suodataTilit(haku: string): Kirjanpitotili[] {
    const trimmattu = (haku ?? '').trim().toLowerCase()
    const ehdokkaat = this._tilitList.filter(t => !_ALV_TILINUMEROT.has(t.numero))
    if (!trimmattu) {
      return ehdokkaat.slice(0, 50)
    }
    return ehdokkaat.filter(t => t.numero.startsWith(trimmattu) || this._tilikarttaJaettuService.annaKirjanpitotilinNimi(t, 'fi').toLowerCase().includes(trimmattu)).slice(0, 50)
  }

  private _annaAktiivisetAlvt(rivi: Kirjausrivi): AlvMaaritys[] {
    const tili = rivi.t ? this._tilitMap.get(rivi.t) : null
    if (tili?.alvTyyppi === KirjanpitotilinAlvTyyppi.MYYNTI) {
      return MyyntiAlvt.kaikki
    }
    if (tili?.alvTyyppi === KirjanpitotilinAlvTyyppi.OSTO) {
      return OstoAlvt.kaikki
    }
    return [...MyyntiAlvt.kaikki, ...OstoAlvt.kaikki]
  }

  private _suodataAlvt(haku: string): AlvMaaritys[] {
    const trimmattu = (haku ?? '').trim().toLowerCase()
    if (!trimmattu) {
      return this._aktiivisetAlvt
    }
    return this._aktiivisetAlvt.filter(a => a.lyhenne.toLowerCase().includes(trimmattu) || a.nimi.toLowerCase().includes(trimmattu))
  }

  // ---------------------------------------------------------------------------
  // Apurit
  // ---------------------------------------------------------------------------

  private _alvLyhenneTunnisteelle(tunniste: string | null): string {
    return tunniste ? (this._alvTunnisteenMukaan.get(tunniste)?.lyhenne ?? '') : ''
  }

  private _formatRaha(arvo: number | null): string {
    return arvo ? this._currencyService.formatoiDesimaaliSailytaNollat(arvo, 2, 'fi') : ''
  }

  private _parseRaha(arvo: string): number | null {
    if (arvo === null || arvo === undefined || arvo.trim() === '') {
      return null
    }
    const numero = Number(arvo.replace(/\s/g, '').replace(',', '.'))
    if (isNaN(numero)) {
      return null
    }
    return this._currencyService.roundHalfUp(numero, 2)
  }

  private async _paivitaAlv(rivi: Kirjausrivi, vanhastaSummasta: boolean) {
    if (!this.muokattavaKirjaus()) {
      return
    }
    await this._kirjausService.paivitaKirjausrivinAlvRivi(rivi, this.muokattavaKirjaus(), vanhastaSummasta, this._tilitMap, this._tilitMap, this._alvFinder, this._docIdProvider)
    this._rakennaRiviMap()
    this._changeDetectorRef.markForCheck()
  }

  private _suljeAutot(input: HTMLInputElement) {
    if (input !== this._tnroInput?.nativeElement) {
      this._suljeTiliAuto()
    }
    if (input !== this._alvInput?.nativeElement) {
      this._suljeAlvAuto()
    }
  }

  private _suljeTiliAuto() {
    if (this._tiliAutocomplete?.isOpen) {
      this._tiliAutocompleteTrigger?.closePanel()
    }
    if (this._tilinumeroSubject.value !== null) {
      this._tilinumeroSubject.next(null)
    }
  }

  private _suljeAlvAuto() {
    if (this._alvAutocomplete?.isOpen) {
      this._alvAutocompleteTrigger?.closePanel()
    }
    if (this._alvSubject.value !== null) {
      this._alvSubject.next(null)
    }
  }

  private _kohdistaSoluun(arvoavain: string, sarake: Solusarake) {
    const luokka = sarake === 'tili' ? 'knc' : sarake === 'debet' ? 'kd' : sarake === 'kredit' ? 'kk' : 'ka'
    const solu = document.querySelector('[data-arvoavain="' + arvoavain + '"] .' + luokka) as HTMLElement
    solu?.focus()
  }

  private _preventEvent(event: Event) {
    event.preventDefault()
    event.stopPropagation()
  }

  private _etsiArvoavain(element: HTMLElement): string | null {
    if (!element) {
      return null
    }
    if (element.dataset.arvoavain) {
      return element.dataset.arvoavain
    }
    if (element.parentElement?.dataset.arvoavain) {
      return element.parentElement.dataset.arvoavain
    }
    if (element.parentElement?.parentElement?.dataset.arvoavain) {
      return element.parentElement.parentElement.dataset.arvoavain
    }
    if (element.parentElement?.parentElement?.parentElement?.dataset.arvoavain) {
      return element.parentElement.parentElement.parentElement.dataset.arvoavain
    }
    return null
  }

  private _etsiKirjausElement(element: HTMLElement): HTMLElement | null {
    let suspect: HTMLElement | null = element
    for (let i = 0; i < 6 && suspect; i++) {
      if (suspect.classList.contains('kl')) {
        return suspect
      }
      suspect = suspect.parentElement
    }
    return null
  }

  private _yksiEnnenRivia(element: HTMLElement): HTMLElement | null {
    let suspect: HTMLElement | null = element
    for (let i = 0; i < 4 && suspect; i++) {
      if (suspect.parentElement?.classList.contains('kr')) {
        return suspect
      }
      suspect = suspect.parentElement
    }
    return null
  }

  private _seuraavaFokusoitavaFrom(input: HTMLElement): HTMLElement | null {
    const yksiEnnen = this._yksiEnnenRivia(input)
    if (!yksiEnnen) {
      return null
    }
    return this._seuraavaFokusoitava(yksiEnnen.nextElementSibling as HTMLElement)
  }

  private _seuraavaFokusoitava(el: HTMLElement | null): HTMLElement | null {
    if (!el) {
      return null
    }
    const element = this._yksiEnnenRivia(el)
    if (!element) {
      return null
    }
    const onSolu = element.classList.contains('knc') || element.classList.contains('kd') || element.classList.contains('kk') || element.classList.contains('ka')
    if (element.style.display !== 'none' && element.hasAttribute('tabindex') && onSolu) {
      return element
    }
    if (element.nextElementSibling) {
      return this._seuraavaFokusoitava(element.nextElementSibling as HTMLElement)
    }
    const nextRow = element.parentElement?.nextElementSibling
    if (nextRow && nextRow.classList.contains('kr') && nextRow.firstElementChild) {
      return this._seuraavaFokusoitava(nextRow.firstElementChild as HTMLElement)
    }
    return null
  }

  // ---------------------------------------------------------------------------
  // Tallennus
  // ---------------------------------------------------------------------------

  async tallennaMuokkaus() {
    if (!this.muokattavaTestitapaus() || !this.muokattavaKirjaus() || !this.muokkausTasapainossa()) {
      return
    }
    const rivit = this.muokattavaKirjaus().rivit.filter(r => !this._onkoRiviTyhja(r))
    this.tallennetaan.set(true)
    try {
      if (this.muokattavaOnUusiVaihtoehto()) {
        const lisaysVastaus = await callLlmTestaus(this._firebase, 'lisaaOikeaVaihtoehto', { testitapausAvain: this.muokattavaTestitapaus().avain, rivit })
        if (lisaysVastaus.e || !lisaysVastaus.avain) {
          this._snackbar.open('Vaihtoehdon tallennus epäonnistui', 'Sulje', { duration: 4000 })
          return
        }
        const vaihtoehdot = this.muokattavaTestitapaus().oikeatVaihtoehdot ?? []
        vaihtoehdot.push({ avain: lisaysVastaus.avain, rivit })
        this.muokattavaTestitapaus().oikeatVaihtoehdot = vaihtoehdot
        this._snackbar.open('Uusi kirjaus vaihtoehto lisätty', 'Sulje', { duration: 3000 })
        this.peruutaMuokkaus()
        return
      }

      if (this.muokattavaVaihtoehtoAvain()) {
        const vaihtoehtoVastaus = await callLlmTestaus(this._firebase, 'paivitaOikeaVaihtoehto', { avain: this.muokattavaVaihtoehtoAvain(), rivit })
        if (vaihtoehtoVastaus.e) {
          this._snackbar.open('Tallennus epäonnistui', 'Sulje', { duration: 4000 })
          return
        }
        const vaihtoehto = this.muokattavaTestitapaus().oikeatVaihtoehdot?.find(v => v.avain === this.muokattavaVaihtoehtoAvain())
        if (vaihtoehto) {
          vaihtoehto.rivit = rivit
        }
        this._snackbar.open('Kirjaus vaihtoehto tallennettu', 'Sulje', { duration: 3000 })
        this.peruutaMuokkaus()
        return
      }

      const tallennettavaKirjaus: Kirjaus = { ...this.muokattavaKirjaus(), rivit }
      const vastaus = await callLlmTestaus(this._firebase, 'paivitaTestitapausKirjaus', { avain: this.muokattavaTestitapaus().avain, kirjaus: tallennettavaKirjaus })
      if (vastaus.e) {
        this._snackbar.open('Tallennus epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      this.muokattavaTestitapaus().kirjaus = tallennettavaKirjaus
      this._snackbar.open('Kirjaus tallennettu', 'Sulje', { duration: 3000 })
      this.peruutaMuokkaus()
    } catch (err) {
      console.error('tallennaMuokkaus virhe', err)
      this._snackbar.open('Tallennus epäonnistui', 'Sulje', { duration: 4000 })
    } finally {
      this.tallennetaan.set(false)
    }
  }

  async poistaVaihtoehto(testitapaus: LlmTestitapaus, vaihtoehto: LlmTestitapausOikeaVaihtoehto, numero: number) {
    const data: AreYouSureDialogData = { text: `Poistetaanko vaihtoehto ${numero}?`, header: 'Poista vaihtoehto', rightAction: 'Poista' }
    const sure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!sure) {
      return
    }
    try {
      const vastaus = await callLlmTestaus(this._firebase, 'poistaOikeaVaihtoehto', { avain: vaihtoehto.avain })
      if (vastaus.e) {
        this._snackbar.open('Poisto epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      testitapaus.oikeatVaihtoehdot = (testitapaus.oikeatVaihtoehdot ?? []).filter(v => v.avain !== vaihtoehto.avain)
      this._changeDetectorRef.markForCheck()
      if (this.muokattavaVaihtoehtoAvain() === vaihtoehto.avain) {
        this.peruutaMuokkaus()
      }
    } catch (err) {
      console.error('poistaVaihtoehto virhe', err)
      this._snackbar.open('Poisto epäonnistui', 'Sulje', { duration: 4000 })
    }
  }
}
